import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { NotifyService } from '@app/services';
import { TranslatableKey } from '@app/types/translatable.type';
import { TimeOffTypeUnitOfTimeDisplayOptions } from '@app/types/translatables/time-off-v3-display.options';
import { TimeOffPolicy } from '@models/time-off-v3/time-off-policy.model';
import { getTime, setTime } from '@time-off-v3/functions/time-unit-converter';
import { TimeOffPreviews } from '@time-off-v3/interfaces/time-off-balance.interface';
import {
    AccrualFrequencies,
    CompareAccrualFrequencies,
    DECIMAL_PLACES,
    TimeOffDateFormatDashes,
} from '@time-off-v3/meta/time-off-meta';
import { TimeOffService } from '@time-off-v3/services';
import { PolicyManageService } from '@time-off-v3/services/policy-v2/policy-manage.service';
import moment from 'moment/moment';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-accrual-schedule-update-card',
    templateUrl: './accrual-schedule-update-card.template.html',
    styleUrls: ['./accrual-schedule-update-card.style.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class AccrualScheduleUpdateCardComponent implements OnInit, OnDestroy {
    @Input() timeOffPolicy = new TimeOffPolicy();
    @Output() onChange = new EventEmitter();

    isLoading = false;
    accrualFrequencies = AccrualFrequencies;

    accrualPreviews: TimeOffPreviews[] = [];
    numberOfPreviews = 3;
    days = 0;
    daysPerPeriod = 0;
    compareAccrualFrequencies = CompareAccrualFrequencies;

    private daysAccruedPerPeriodChangedSubjectSubscription!: Subscription;

    constructor(
        private readonly timeOffService: TimeOffService,
        private policyManageService: PolicyManageService,
        private notify: NotifyService
    ) {}

    ngOnInit(): void {
        this.daysAccruedPerPeriodChangedSubjectSubscription =
            this.policyManageService.daysAccruedPerPeriodChangedSubject$.subscribe(() => {
                this.setDaysPerYear();
                this.calculateDaysForPeriod();
            });

        if (!this.timeOffPolicy.isPersisted) {
            return;
        }

        this.getAccrualPreview(this.timeOffPolicy.accrualFrequency);
        this.setDaysPerYear();
        this.calculateDaysForPeriod();
    }

    ngOnDestroy(): void {
        this.daysAccruedPerPeriodChangedSubjectSubscription.unsubscribe();
    }

    get unitOfTime(): string | TranslatableKey {
        const unit = this.timeOffPolicy?.timeOffType?.unitOfTimePlural;
        return TimeOffTypeUnitOfTimeDisplayOptions[unit] ?? unit;
    }

    calculateDaysForPeriod(): void {
        this.daysPerPeriod = 0;
        // Round to 3 decimal places because of business requirements.
        if (this.days !== null && this.timeOffPolicy.accrualFrequency) {
            const daysPerPeriod = this.days / this.accrualFrequencies[this.timeOffPolicy.accrualFrequency];
            this.timeOffPolicy.daysAccruedPerPeriod = setTime(daysPerPeriod, this.timeOffPolicy?.timeOffType);
            this.daysPerPeriod = +daysPerPeriod.toFixed(DECIMAL_PLACES);
            this.onChange.emit();
        }
    }

    async getAccrualPreview(frequency: string): Promise<void> {
        if (this.timeOffPolicy.hasAccrualValuesSet(frequency)) {
            try {
                const accrualPreviews = await this.timeOffService.getAccrualPreview(this.timeOffPolicy);

                this.accrualPreviews = accrualPreviews.slice(0, this.numberOfPreviews);

                this.onChange.emit();
            } catch (err) {
                this.notify.error(err);
            }
        }
    }

    getAccrualHappenOnDate(startDate: string, endDate: string): string {
        if (this.timeOffPolicy.accrualTiming === 'first-day') {
            return startDate;
        }

        if (this.timeOffPolicy.accrualTiming === 'last-day') {
            return endDate;
        }

        if (this.timeOffPolicy.accrualTiming === 'next-first-day') {
            return moment(endDate, TimeOffDateFormatDashes).add(1, 'days').format(TimeOffDateFormatDashes);
        }
    }

    private setDaysPerYear(): void {
        this.days = +getTime(
            +(this.timeOffPolicy.daysAccruedPerPeriod * this.accrualFrequencies[this.timeOffPolicy.accrualFrequency]),
            this.timeOffPolicy.timeOffType
        ).toFixed(DECIMAL_PLACES);
    }
}
